import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Footer from "../../../components/Footer";

/* ---------------- SERVICES DATA ---------------- */
const services = [
  {
    title: "Governance, Risk & Compliance",
    desc: "Cost effective solutions for mitigating risks and achieving compliance goals",
    link: "/grc",
  },
  {
    title: "Security Awareness",
    desc: "High impact campaigns to empower and educate your workforce",
    link: "/security-awareness",
  },
  {
    title: "Strategy & Management",
    desc: "Pragmatic security programs and strategies including our proven vCISO offering",
    link: "/strategy",
  },
  {
    title: "Penetration Testing",
    desc: "Verify how well applications, websites, networks, devices and buildings are really protected",
    link: "/pentesting",
  },
  {
    title: "Security Architecture",
    desc: "Get the best possible return on investment from your security technologies",
    link: "/architecture",
  },
  {
    title: "Incident Response Planning",
    desc: "Preparation, planning and simulation to ensure your organization can respond quickly and effectively to cyber incidents.",
    link: "/incident-response-plan",
  },
];

const HERO = {
  title: "Cyber Security For Good",
  subtitle:
    "World-class cyber security consulting with a tangible social and environmental impact",
};

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function SecurityConsultingAdvisory() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Security Consulting & Advisory Services | Synthera Security
        </title>

        <meta
          name="description"
          content="Expert cyber security consulting and advisory services including governance, risk management, security awareness, penetration testing, security architecture, and incident response planning."
        />

        <meta
          name="keywords"
          content="Cyber Security Consulting, Security Advisory, Governance Risk Compliance, Penetration Testing, Security Architecture, vCISO, Security Awareness, Incident Response Planning, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Security Consulting & Advisory Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="World-class cyber security consulting designed to reduce risk, improve resilience, and enable business growth."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/security-consulting-advisory"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/security-consulting-advisory"
        />
      </Helmet>

      <div className="min-h-screen font-sans">
        {/* ================= HERO SECTION ================= */}
        <section className="bg-[#032d28] pt-40 pb-44 px-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-10 
        bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),
        linear-gradient(to_bottom,#80808012_1px,transparent_1px)] 
        bg-[size:40px_40px]"
          />
          <div className="container mx-auto max-w-5xl text-center relative z-10">
            <motion.div initial="hidden" animate="visible" variants={FADE_UP}>
              <h1 className="text-white text-4xl md:text-7xl font-bold leading-[1.1] mb-10 tracking-tight">
                {HERO.title}
              </h1>
              <div className="w-24 h-1 bg-[#24ff81] mx-auto mb-10" />
              <p className="text-emerald-100/90 text-2xl md:text-3xl leading-snug max-w-4xl mx-auto font-bold">
                {HERO.subtitle}
              </p>
            </motion.div>
          </div>
        </section>
        {/* ================= SERVICES SECTION ================= */}
        <section className="py-24 bg-white relative overflow-hidden">
          {/* subtle background grid */}
          <div
            className="absolute inset-0 opacity-10 
    bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),
    linear-gradient(to_bottom,#80808012_1px,transparent_1px)] 
    bg-[size:40px_40px]"
          />

          {/* HEADER */}
          <div className="max-w-7xl mx-auto text-center mb-16 px-6 relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#071c2c] tracking-tight">
              Our Services
            </h2>

            <p className="text-[#071c2c]/70 text-lg max-w-2xl mx-auto leading-relaxed">
              We pride ourselves on delivering services that make cyber security
              a business enabler, not an obstacle.
            </p>
          </div>

          {/* CARDS GRID — NO GAP */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 relative z-10">
            {services.map((service, i) => (
              <Link to={service.link} key={i} className="no-underline">
                <div
                  className="group relative h-[250px] p-8 flex flex-col justify-between
          bg-[#031f1c] 
          border border-white/10
          transition-all duration-500 ease-out
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_40px_rgba(36,255,129,0.15)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  {/* content */}
                  <div className="relative z-10">
                    <h3 className="text-xl font-semibold text-white mb-3 tracking-tight">
                      {service.title}
                    </h3>

                    <p className="text-white/70 text-sm leading-relaxed">
                      {service.desc}
                    </p>
                  </div>

                  {/* bottom */}
                  <div className="relative z-10 flex items-center justify-between mt-6">
                    <span className="text-xs tracking-widest text-emerald-300/80">
                      EXPLORE
                    </span>

                    <div
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center
              group-hover:border-emerald-400 group-hover:bg-emerald-400/10
              transition-all duration-300"
                    >
                      <span className="text-white group-hover:text-emerald-300 text-lg transition-transform duration-300 group-hover:translate-x-1">
                        →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* footer */}
        {/* <div className="text-center mt-12">
          <p className="text-[#071c2c]/80 text-sm">
            Further information can be found in our{" "}
            <Link
              to="/catalogue"
              className="underline underline-offset-4 hover:text-emerald-600 font-medium"
            >
              service catalogue
            </Link>
            .
          </p>
        </div> */}

        {/* ================= CERTIFICATIONS SECTION ================= */}

        <section className="bg-white py-24 px-6 overflow-hidden">
          <div className="max-w-6xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Certifications
            </h2>
            <p className="text-slate-600 text-lg max-w-3xl mx-auto leading-relaxed">
              A small sample of some of our certifications and awards are listed
              below.
            </p>
          </div>

          {/* LOGO SCROLLING CONTAINER */}
          <div className="relative max-w-6xl mx-auto">
            {/* LEFT SIDE GRADIENT MASK (Fades from White) */}
            <div className="absolute inset-y-0 left-0 w-32 md:w-64 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />

            {/* RIGHT SIDE GRADIENT MASK (Fades to White) */}
            <div className="absolute inset-y-0 right-0 w-32 md:w-64 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />

            {/* ROW 1: SCROLL LEFT */}
            <div className="relative w-full overflow-hidden mb-12">
              <div className="flex whitespace-nowrap gap-16 animate-marquee">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex gap-10 items-center shrink-0">
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905385/4f196e_ac7e5e2806ea4a01a3164ce559683443_mv2_hf7dqu.png"
                      alt="ISO"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905392/4f196e_46ecbb0f8cb049d4a3de20206bac713b_mv2_zgvewy.png"
                      alt="IRAP"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905385/4f196e_8ac7b8e1946342acbaa1a8f2de1e2d73_mv2_qjybnv.png"
                      alt="CREST"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905384/4f196e_fc9020d7b7cb47ab859a86f401f74ffe_mv2_o2gcae.png"
                      alt="CISA"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* ROW 2: SCROLL RIGHT */}
            <div className="relative w-full overflow-hidden">
              <div className="flex whitespace-nowrap gap-16 animate-marquee-reverse">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex gap-10 items-center shrink-0">
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905384/4f196e_0378f5ae39f14249b143e5d6f3ffaf17_mv2_f0mgad.png"
                      alt="CRISC"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905384/4f196e_2bb95b388ab94794bf02eb05a525b856_mv2_tibelv.png"
                      alt="CISM"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905384/4f196e_c57a8696620141adb3994f5c5c7b9ac7_mv2_uikchu.png"
                      alt="CISSP"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1770905385/4f196e_32eb035e1f3d44d08792a1e561ffea63_mv2_prbcqj.png"
                      alt="AWS"
                      className="h-16 w-auto object-contain transition-transform hover:scale-110 duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* TAILWIND ANIMATION STYLES */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
    @keyframes marquee {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    @keyframes marquee-reverse {
      0% { transform: translateX(-50%); }
      100% { transform: translateX(0); }
    }
    .animate-marquee {
      animation: marquee 40s linear infinite;
    }
    .animate-marquee-reverse {
      animation: marquee-reverse 40s linear infinite;
    }
    .animate-marquee:hover, 
    .animate-marquee-reverse:hover {
      animation-play-state: paused;
    }
  `,
            }}
          />
        </section>
        <Footer />
      </div>
    </>
  );
}
